import type {WorkstreamBasic} from 'client';
import type {RecentStripItem} from 'views/workstreams/page/recentsStripData';

const latestUpdateAt = (workstream: WorkstreamBasic) =>
  Math.max(0, ...(workstream.updates ?? []).map(({created_at}) => Date.parse(created_at) || 0));

const byLatestUpdate = (a: WorkstreamBasic, b: WorkstreamBasic) =>
  latestUpdateAt(b) - latestUpdateAt(a);

// The tracker returns workstreams you can't open as "Restricted" stand-ins
// (status_cadence 'none'; real ones with that cadence are filtered out).
const isRestricted = (workstream: WorkstreamBasic) => workstream.status_cadence === 'none';

// Slots for /workstreams pins and recents together; pins fill them first.
export const MAX_SEARCH_VISITED = 4;

/**
 * What global search lists for workstreams before you type: the workstreams
 * you're DRI on (by latest update), then up to MAX_SEARCH_VISITED of your
 * /workstreams pins and recents (pins first, in the rail's order), then the
 * rest of yours (by latest update). A recent that isn't yours resolves through
 * `recentRows`; one that hasn't loaded or is archived is left out.
 */
export const rankSearchWorkstreams = ({
  recents,
  mine,
  recentRows,
  myLdap,
}: {
  recents: RecentStripItem[];
  mine: WorkstreamBasic[];
  recentRows?: Map<number, WorkstreamBasic>;
  myLdap?: string;
}): WorkstreamBasic[] => {
  const usable = mine.filter((workstream) => !isRestricted(workstream));
  const mineById = new Map(usable.map((workstream) => [workstream.id, workstream]));
  const resolve = (items: RecentStripItem[]) =>
    items
      .filter((item) => item.type === 'workstream')
      .map((item) => mineById.get(item.id) ?? recentRows?.get(item.id))
      .filter((workstream): workstream is WorkstreamBasic =>
        Boolean(workstream && !workstream.archived)
      );
  const isMyDri = (workstream: WorkstreamBasic) =>
    workstream.employee_associations.some(
      ({dri, employee}) => dri === 'dri' && employee.ldap === myLdap
    );

  const dri = usable.filter(isMyDri).sort(byLatestUpdate);
  const driIds = new Set(dri.map(({id}) => id));
  const pinsThenRecents = [
    ...recents.filter((item) => item.pinned),
    ...recents.filter((item) => !item.pinned),
  ];
  const visited = resolve(pinsThenRecents)
    .filter(({id}) => !driIds.has(id))
    .slice(0, MAX_SEARCH_VISITED);
  const shownIds = new Set([...driIds, ...visited.map(({id}) => id)]);
  const rest = usable.filter(({id}) => !shownIds.has(id)).sort(byLatestUpdate);
  return [...dri, ...visited, ...rest];
};
